import React, { useState, useRef, useCallback } from "react";
import {
  IconX,
  IconUpload,
  IconDownload,
  IconFileSpreadsheet,
  IconChevronDown,
  IconChevronUp,
  IconAlertCircle,
  IconCircleCheck,
  IconTrash,
} from "@tabler/icons-react";
import toast from "react-hot-toast";

interface ImportAttendanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport?: (file: File) => Promise<void>;
  isSidebar?: boolean;
}

const IMPORT_FIELDS = [
  { field: "employeeId", required: true, description: "Unique employee identifier" },
  { field: "employeeName", required: false, description: "Full name of the employee" },
  { field: "attendanceDate", required: true, description: "Date in YYYY-MM-DD format" },
  { field: "clockIn", required: false, description: "Clock-in time (HH:MM)" },
  { field: "clockOut", required: false, description: "Clock-out time (HH:MM)" },
  { field: "status", required: true, description: "PRESENT / ABSENT / LATE / LEAVE / WEEK_OFF / HOLIDAY" },
  { field: "isLate", required: false, description: "true / false" },
  { field: "isHalfDay", required: false, description: "true / false" },
  { field: "workingFrom", required: false, description: "Office / Home / Field" },
  { field: "remarks", required: false, description: "Any additional remarks" },
];

export default function ImportAttendanceModal({
  isOpen,
  onClose,
  onImport,
  isSidebar = false,
}: ImportAttendanceModalProps) {
  const [showFields, setShowFields] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const ACCEPTED_TYPES = [
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel",
    "application/vnd.ms-excel.sheet.macroEnabled.12",
    "text/csv",
  ];
  const MAX_FILE_SIZE_MB = 20;

  const validateFile = (file: File): string | null => {
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      return `File exceeds ${MAX_FILE_SIZE_MB}MB limit.`;
    }
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!["xlsx", "xls", "xlsm", "csv"].includes(ext || "")) {
      return "Only .xlsx, .xls, .xlsm, or .csv files are allowed.";
    }
    return null;
  };

  const handleFileSelect = (file: File) => {
    const err = validateFile(file);
    if (err) {
      toast.error(err);
      return;
    }
    setSelectedFile(file);
  };

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files?.[0];
      if (file) handleFileSelect(file);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
    e.target.value = "";
  };

  const handleRemoveFile = () => setSelectedFile(null);

  const handleDownloadSample = () => {
    // Create a simple CSV sample
    const headers = IMPORT_FIELDS.map((f) => f.field).join(",");
    const sampleRow = [
      "EMP-001",
      "John Doe",
      "2026-07-14",
      "09:00",
      "18:00",
      "PRESENT",
      "false",
      "false",
      "Office",
      "On time",
    ].join(",");
    const csvContent = `${headers}\n${sampleRow}`;
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "attendance_import_sample.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Sample file downloaded!");
  };

  const handleOpenEditor = async () => {
    if (!selectedFile) {
      toast.error("Please select a file first.");
      return;
    }
    setIsUploading(true);
    try {
      if (onImport) {
        await onImport(selectedFile);
      } else {
        // Simulate upload
        await new Promise((r) => setTimeout(r, 1200));
        toast.success("File imported successfully!");
      }
      onClose();
      setSelectedFile(null);
    } catch (err: any) {
      toast.error(err?.message || "Failed to import file.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setShowFields(false);
    setIsDragging(false);
    onClose();
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  if (!isOpen) return null;

  const innerContent = (
    <div
      className={
        isSidebar
          ? "flex flex-col bg-white overflow-hidden h-full w-full animate-in fade-in duration-200"
          : "relative bg-white rounded-2xl shadow-2xl w-full max-w-lg border border-gray-100 animate-in fade-in zoom-in-95 duration-200"
      }
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-[#FBD037]/20 flex items-center justify-center">
            <IconUpload size={16} className="text-[#C9A100]" />
          </div>
          <h2 className="text-base font-bold text-[#212121]">Import Data</h2>
        </div>
        <button
          onClick={handleClose}
          className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors text-slate-500 cursor-pointer"
        >
          <IconX size={16} />
        </button>
      </div>

      {/* Body */}
      <div className="p-6 space-y-4 overflow-y-auto flex-1">
        {/* Notice about required fields */}
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 space-y-1.5">
          <p className="text-xs font-semibold text-blue-700">
            Before uploading the sheet, please ensure that the following fields are included in
            the Excel file:
          </p>
          <p className="text-[11px] text-blue-600">
            Make sure headers match exactly (case-insensitive). Missing fields may cause import
            errors.
          </p>
          <button
            type="button"
            onClick={() => setShowFields((v) => !v)}
            className="inline-flex items-center gap-1.5 text-[11px] font-bold text-blue-600 hover:text-blue-800 transition-colors mt-0.5 cursor-pointer"
          >
            {showFields ? (
              <>
                <IconChevronUp size={13} /> Hide fields
              </>
            ) : (
              <>
                <IconChevronDown size={13} /> Show fields
              </>
            )}
          </button>

          {/* Fields list */}
          {showFields && (
            <div className="mt-3 border border-blue-100 rounded-xl overflow-hidden">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="bg-blue-100/60">
                    <th className="text-left px-3 py-2 font-bold text-blue-700">Field</th>
                    <th className="text-left px-3 py-2 font-bold text-blue-700">Required</th>
                    <th className="text-left px-3 py-2 font-bold text-blue-700">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-blue-50">
                  {IMPORT_FIELDS.map((f) => (
                    <tr key={f.field} className="bg-white hover:bg-blue-50/30 transition-colors">
                      <td className="px-3 py-2 font-mono font-semibold text-slate-700">
                        {f.field}
                      </td>
                      <td className="px-3 py-2">
                        {f.required ? (
                          <span className="inline-flex items-center gap-1 text-red-600 font-bold">
                            <IconAlertCircle size={11} /> Yes
                          </span>
                        ) : (
                          <span className="text-gray-400">No</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-500">{f.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Sample file download */}
        <div className="flex items-center justify-between bg-slate-50 border border-slate-100 rounded-xl px-4 py-3">
          <div>
            <p className="text-xs font-semibold text-slate-700">
              Need a sample file to understand the import format?
            </p>
            <p className="text-[11px] text-gray-400 mt-0.5">
              Download the template and upload your data in the same structure.
            </p>
          </div>
          <button
            type="button"
            onClick={handleDownloadSample}
            className="ml-3 flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#FBD037] bg-[#FBD037]/10 text-[11px] font-bold text-[#212121] hover:bg-[#FBD037]/20 transition-colors whitespace-nowrap cursor-pointer"
          >
            <IconDownload size={13} />
            Sample File
          </button>
        </div>

        {/* File Drop Zone */}
        {!selectedFile ? (
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center gap-3 transition-all duration-200 cursor-pointer ${
              isDragging
                ? "border-[#FBD037] bg-[#FBD037]/5 scale-[1.01]"
                : "border-gray-200 hover:border-[#FBD037] hover:bg-gray-50/50"
            }`}
          >
            <IconFileSpreadsheet
              size={36}
              className={`transition-colors ${isDragging ? "text-[#C9A100]" : "text-gray-300"}`}
            />
            <p className="text-xs font-semibold text-gray-500">
              {isDragging ? "Drop your file here!" : "Click or drag an Excel file here"}
            </p>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current?.click();
              }}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-200 bg-white text-[11px] font-bold text-slate-600 hover:border-[#FBD037] hover:text-[#212121] transition shadow-sm cursor-pointer"
            >
              <IconUpload size={13} />
              Choose file
            </button>
            <p className="text-[10px] text-gray-400">
              Allowed: .xlsx, .xls, .xlsm .csv &nbsp;·&nbsp; Max {MAX_FILE_SIZE_MB}MB
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.xlsm,.csv"
              className="hidden"
              onChange={handleInputChange}
            />
          </div>
        ) : (
          /* File preview */
          <div className="border border-emerald-200 bg-emerald-50 rounded-xl px-4 py-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <IconCircleCheck size={20} className="text-emerald-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-[#212121] truncate">{selectedFile.name}</p>
              <p className="text-[11px] text-gray-400">{formatFileSize(selectedFile.size)}</p>
            </div>
            <button
              type="button"
              onClick={handleRemoveFile}
              className="p-1.5 rounded-lg border border-red-100 bg-white hover:bg-red-50 text-red-400 transition cursor-pointer"
              title="Remove file"
            >
              <IconTrash size={14} />
            </button>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 shrink-0">
        <button
          type="button"
          onClick={handleClose}
          className="px-5 py-2 rounded-xl border border-gray-200 bg-white text-xs font-semibold text-slate-600 hover:bg-gray-50 transition cursor-pointer"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleOpenEditor}
          disabled={!selectedFile || isUploading}
          className={`px-5 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 cursor-pointer ${
            selectedFile && !isUploading
              ? "bg-[#212121] hover:bg-slate-800 text-white shadow-sm"
              : "bg-gray-100 text-gray-400 cursor-not-allowed"
          }`}
        >
          {isUploading && (
            <svg
              className="animate-spin h-3.5 w-3.5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
            </svg>
          )}
          {isUploading ? "Uploading..." : "Open Editor"}
        </button>
      </div>
    </div>
  );

  if (isSidebar) {
    return innerContent;
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={handleClose}
      >
        {innerContent}
      </div>
    </>
  );
}
